import type { VideoFile } from "@/types";
import type { TimelineRange } from "@/utils/sharedTimeline";

export interface VideoSessionClip {
  video: VideoFile;
  recordedAt: string;
  duration: number;
  startTime: number;
  endTime: number;
}

export interface VideoSessionTimeline {
  anchorTimestamp: string;
  clips: VideoSessionClip[];
  range: TimelineRange;
}

export const TIMELINE_EPSILON_SECONDS = 0.05;

const getTimestampMs = (timestamp: string | null | undefined): number | null => {
  if (!timestamp) {
    return null;
  }

  const timestampMs = new Date(timestamp).getTime();
  return Number.isFinite(timestampMs) ? timestampMs : null;
};

const getVideoDuration = (
  video: VideoFile,
  durations: Record<string, number>,
): number | null => {
  const duration = durations[video.id] ?? video.duration;
  return typeof duration === "number" && Number.isFinite(duration) && duration > 0
    ? duration
    : null;
};

export const buildVideoSessionTimeline = (
  videos: VideoFile[],
  durations: Record<string, number> = {},
  fallbackAnchorTimestamp?: string | null,
): VideoSessionTimeline | null => {
  const candidates = videos.flatMap((video) => {
    const recordedAt = video.recordedAt ??
      (videos.length === 1 ? fallbackAnchorTimestamp : null);
    const timestampMs = getTimestampMs(recordedAt);
    const duration = getVideoDuration(video, durations);

    if (!recordedAt || timestampMs === null || duration === null) {
      return [];
    }

    return [{ video, recordedAt, timestampMs, duration }];
  });

  if (candidates.length === 0) {
    return null;
  }

  const anchorTimestampMs = Math.min(
    ...candidates.map((candidate) => candidate.timestampMs),
  );
  const ordered = candidates
    .map<VideoSessionClip>((candidate) => {
      const startTime = (candidate.timestampMs - anchorTimestampMs) / 1000;
      return {
        video: candidate.video,
        recordedAt: candidate.recordedAt,
        duration: candidate.duration,
        startTime,
        endTime: startTime + candidate.duration,
      };
    })
    .sort(
      (left, right) =>
        left.startTime - right.startTime ||
        left.video.filename.localeCompare(right.video.filename),
    );

  // Sequential clips must never overlap on the timeline. Because recordedAt
  // is not a reliable per-file start across sources (it is the recording END
  // for some devices — see #477), push each clip to start no earlier than the
  // previous clip's end. Genuine gaps (next clip starts later) are preserved.
  const clips: VideoSessionClip[] = [];
  for (const clip of ordered) {
    const previous = clips[clips.length - 1];
    const startTime = previous
      ? Math.max(clip.startTime, previous.endTime)
      : clip.startTime;
    clips.push({ ...clip, startTime, endTime: startTime + clip.duration });
  }
  const end = Math.max(...clips.map((clip) => clip.endTime));

  return {
    anchorTimestamp: new Date(anchorTimestampMs).toISOString(),
    clips,
    range: { start: 0, end },
  };
};

export const getVideoSessionClipAtTime = (
  timeline: VideoSessionTimeline | null,
  sessionTime: number,
  preferredVideoId?: string | null,
): VideoSessionClip | null => {
  if (!timeline || !Number.isFinite(sessionTime)) {
    return null;
  }

  const matchingClips = timeline.clips.filter(
    (clip) =>
      sessionTime >= clip.startTime - TIMELINE_EPSILON_SECONDS &&
      sessionTime <= clip.endTime + TIMELINE_EPSILON_SECONDS,
  );

  if (matchingClips.length === 0) {
    return null;
  }

  const preferredClip = preferredVideoId
    ? matchingClips.find((clip) => clip.video.id === preferredVideoId)
    : null;
  if (preferredClip && sessionTime < preferredClip.endTime) {
    return preferredClip;
  }

  return matchingClips.reduce((latest, clip) =>
    clip.startTime >= latest.startTime ? clip : latest,
  );
};

export const getNextVideoSessionClip = (
  timeline: VideoSessionTimeline | null,
  sessionTime: number,
): VideoSessionClip | null =>
  timeline?.clips.find(
    (clip) => clip.startTime > sessionTime + TIMELINE_EPSILON_SECONDS,
  ) ?? null;

export const getVideoLocalTime = (
  clip: VideoSessionClip,
  sessionTime: number,
): number => Math.min(Math.max(sessionTime - clip.startTime, 0), clip.duration);

export const getVideoSessionTime = (
  clip: VideoSessionClip,
  localTime: number,
): number => clip.startTime + Math.min(Math.max(localTime, 0), clip.duration);
